import React, { useState, useEffect, useContext } from "react";
import { AuthContext, API } from "../App";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { MultiSelect } from "./ui/MultiSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Plus,
  Edit,
  Trash2,
  MoreVertical,
  Settings,
  Briefcase,
  Building,
  CheckCircle,
  XCircle,
  Search,
  Download,
  Upload,
  FileText,
  AlertCircle
} from "lucide-react";
import { toast } from "sonner";

export default function ManageLists() {
  const { user } = useContext(AuthContext);
  const [trades, setTrades] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [sites, setSites] = useState([]);
  const [areas, setAreas] = useState([]);
  const [elements, setElements] = useState([]);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTradeDialog, setShowTradeDialog] = useState(false);
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [showAreaDialog, setShowAreaDialog] = useState(false);
  const [showElementDialog, setShowElementDialog] = useState(false);
  const [showIssueDialog, setShowIssueDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [activeTab, setActiveTab] = useState("trades");

  const [tradeForm, setTradeForm] = useState({
    name: "",
    description: "",
    category: "general",
    is_active: true
  });

  const [companyForm, setCompanyForm] = useState({
    name: "",
    contact_person: "",
    phone: "",
    email: "",
    address: "",
    company_type: "contractor",
    site_ids: [],
    is_active: true
  });

  const [areaForm, setAreaForm] = useState({
    name: ""
  });

  const [elementForm, setElementForm] = useState({
    name: ""
  });

  const [issueForm, setIssueForm] = useState({
    name: ""
  });

  // Import dialog states
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [selectedImportSites, setSelectedImportSites] = useState([]);
  
  // Duplicate review states
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateCompanies, setDuplicateCompanies] = useState([]);
  const [selectedDuplicates, setSelectedDuplicates] = useState({});

  const [searchTerm, setSearchTerm] = useState("");

  const tradeCategories = [
    { value: "general", label: "General" },
    { value: "structural", label: "Structural" },
    { value: "electrical", label: "Electrical" },
    { value: "plumbing", label: "Plumbing" },
    { value: "hvac", label: "HVAC" },
    { value: "finishing", label: "Finishing" }
  ];

  const companyTypes = [
    { value: "contractor", label: "Contractor" },
    { value: "subcontractor", label: "Subcontractor" },
    { value: "supplier", label: "Supplier" },
    { value: "client", label: "Client" }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      
      // Load trades
      const tradesResponse = await fetch(`${API}/trades?active_only=false`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (tradesResponse.ok) {
        const tradesData = await tradesResponse.json();
        setTrades(tradesData);
      }

      // Load companies
      const companiesResponse = await fetch(`${API}/companies?active_only=false`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (companiesResponse.ok) {
        const companiesData = await companiesResponse.json();
        setCompanies(companiesData);
      }

      // Load sites
      const sitesResponse = await fetch(`${API}/sites`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (sitesResponse.ok) {
        const sitesData = await sitesResponse.json();
        setSites(sitesData);
      }

      // Load areas
      const areasResponse = await fetch(`${API}/defect-areas`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (areasResponse.ok) {
        const areasData = await areasResponse.json();
        setAreas(areasData);
      }

      // Load elements
      const elementsResponse = await fetch(`${API}/defect-elements`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (elementsResponse.ok) {
        const elementsData = await elementsResponse.json();
        setElements(elementsData);
      }

      // Load issues
      const issuesResponse = await fetch(`${API}/defect-issues`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (issuesResponse.ok) {
        const issuesData = await issuesResponse.json();
        setIssues(issuesData);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTrade = async (e) => {
    e.preventDefault();
    
    if (!tradeForm.name.trim()) {
      toast.error("Trade name is required");
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const url = editingItem ? `${API}/trades/${editingItem.id}` : `${API}/trades`;
      const method = editingItem ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(tradeForm)
      });

      if (response.ok) {
        toast.success(editingItem ? "Trade updated successfully!" : "Trade created successfully!");
        setShowTradeDialog(false);
        setEditingItem(null);
        resetTradeForm();
        loadData();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to save trade");
      }
    } catch (error) {
      console.error("Error saving trade:", error);
      toast.error("Failed to save trade");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    
    if (!companyForm.name.trim()) {
      toast.error("Company name is required");
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const url = editingItem ? `${API}/companies/${editingItem.id}` : `${API}/companies`;
      const method = editingItem ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(companyForm)
      });

      if (response.ok) {
        toast.success(editingItem ? "Company updated successfully!" : "Company created successfully!");
        setShowCompanyDialog(false);
        setEditingItem(null);
        resetCompanyForm();
        loadData();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to save company");
      }
    } catch (error) {
      console.error("Error saving company:", error);
      toast.error("Failed to save company");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditTrade = (trade) => {
    setEditingItem(trade);
    setTradeForm({
      name: trade.name,
      description: trade.description || "",
      category: trade.category || "general",
      is_active: trade.is_active
    });
    setShowTradeDialog(true);
  };

  const handleEditCompany = (company) => {
    setEditingItem(company);
    setCompanyForm({
      name: company.name,
      contact_person: company.contact_person || "",
      phone: company.phone || "",
      email: company.email || "",
      address: company.address || "",
      company_type: company.company_type || "contractor",
      site_ids: company.site_ids || [],
      is_active: company.is_active
    });
    setShowCompanyDialog(true);
  };

  // Map a company's site_ids to readable site names; "Unallocated" if none.
  const getCompanySiteNames = (company) => {
    const ids = company.site_ids || [];
    if (ids.length === 0) return [];
    return ids
      .map((id) => sites.find((s) => s.id === id)?.name)
      .filter(Boolean);
  };

  const handleDelete = async () => {
    if (!deleteItem) return;

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      let endpoint;
      
      switch(deleteItem.type) {
        case "trade":
          endpoint = "trades";
          break;
        case "company":
          endpoint = "companies";
          break;
        case "area":
          endpoint = "defect-areas";
          break;
        case "element":
          endpoint = "defect-elements";
          break;
        case "issue":
          endpoint = "defect-issues";
          break;
        default:
          endpoint = deleteItem.type === "trade" ? "trades" : "companies";
      }
      
      const response = await fetch(`${API}/${endpoint}/${deleteItem.id}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        setShowDeleteDialog(false);
        setDeleteItem(null);
        loadData();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to delete item");
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Failed to delete item");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetTradeForm = () => {
    setTradeForm({
      name: "",
      description: "",
      category: "general",
      is_active: true
    });
  };

  const resetCompanyForm = () => {
    setCompanyForm({
      name: "",
      contact_person: "",
      phone: "",
      email: "",
      address: "",
      company_type: "contractor",
      site_ids: [],
      is_active: true
    });
  };

  const resetAreaForm = () => {
    setAreaForm({ name: "" });
  };

  const resetElementForm = () => {
    setElementForm({ name: "" });
  };

  const resetIssueForm = () => {
    setIssueForm({ name: "" });
  };

  // CSV Export for Companies
  const handleExportCompanies = () => {
    try {
      // Create CSV header
      const headers = ['Name', 'Contact Person', 'Phone', 'Email', 'Address', 'Company Type', 'Site Names', 'Active'];
      
      // Create CSV rows
      const rows = companies.map(company => {
        // Get site names from site IDs
        const siteNames = (company.site_ids || [])
          .map(siteId => {
            const site = sites.find(s => s.id === siteId);
            return site ? site.name : '';
          })
          .filter(name => name)
          .join('; '); // Use semicolon as separator for multiple sites
        
        return [
          company.name || '',
          company.contact_person || '',
          company.phone || '',
          company.email || '',
          company.address || '',
          company.company_type || '',
          siteNames,
          company.is_active ? 'Yes' : 'No'
        ];
      });
      
      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell.toString().replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `companies_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Exported ${companies.length} companies to CSV`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export companies');
    }
  };

  // CSV Import for Companies - Parse and check for duplicates
  const handleImportCompanies = async (file, additionalSiteIds = []) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          toast.error('CSV file is empty or invalid');
          return;
        }

        // Parse CSV (simple parser, handles quoted fields)
        const parseCSVLine = (line) => {
          const result = [];
          let current = '';
          let inQuotes = false;
          
          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            const nextChar = line[i + 1];
            
            if (char === '"') {
              if (inQuotes && nextChar === '"') {
                current += '"';
                i++;
              } else {
                inQuotes = !inQuotes;
              }
            } else if (char === ',' && !inQuotes) {
              result.push(current);
              current = '';
            } else {
              current += char;
            }
          }
          result.push(current);
          return result.map(field => field.trim());
        };

        // Parse all companies from CSV
        const dataLines = lines.slice(1);
        const parsedCompanies = [];
        const duplicates = [];

        for (const line of dataLines) {
          if (!line.trim()) continue;

          const fields = parseCSVLine(line);
          if (fields.length < 8) continue;

          const [name, contactPerson, phone, email, address, companyType, siteNames, active] = fields;

          if (!name) continue;

          // Map site names to site IDs from CSV
          const csvSiteIds = siteNames
            ? siteNames.split(';').map(siteName => {
                const trimmedName = siteName.trim();
                const site = sites.find(s => s.name.toLowerCase() === trimmedName.toLowerCase());
                return site ? site.id : null;
              }).filter(id => id)
            : [];

          // Merge CSV sites with additional selected sites (remove duplicates)
          const allSiteIds = [...new Set([...csvSiteIds, ...additionalSiteIds])];

          // Create company object
          const companyData = {
            name: name,
            contact_person: contactPerson || '',
            phone: phone || '',
            email: email || '',
            address: address || '',
            company_type: companyType || 'contractor',
            site_ids: allSiteIds,
            is_active: active.toLowerCase() === 'yes'
          };

          // Check for duplicate (case-insensitive)
          const existingCompany = companies.find(c => 
            c.name.toLowerCase() === name.toLowerCase()
          );

          if (existingCompany) {
            duplicates.push({
              csvData: companyData,
              existing: existingCompany
            });
          } else {
            parsedCompanies.push(companyData);
          }
        }

        // If duplicates found, show review dialog
        if (duplicates.length > 0) {
          setDuplicateCompanies(duplicates);
          // Pre-select all duplicates for import by default
          const preSelected = {};
          duplicates.forEach((dup, index) => {
            preSelected[index] = false; // Default to NOT importing duplicates
          });
          setSelectedDuplicates(preSelected);
          setShowDuplicateDialog(true);
          
          // Store parsed companies for later import
          window.tempParsedCompanies = parsedCompanies;
          window.tempAdditionalSiteIds = additionalSiteIds;
        } else {
          // No duplicates, import directly
          await performImport(parsedCompanies);
        }
      } catch (error) {
        console.error('Import error:', error);
        toast.error('Failed to import companies');
      }
    };

    reader.readAsText(file);
  };

  // Perform actual import
  const performImport = async (companiesToImport) => {
    const token = localStorage.getItem('token');
    let successCount = 0;
    let errorCount = 0;

    for (const companyData of companiesToImport) {
      try {
        const response = await fetch(`${API}/companies`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(companyData)
        });

        if (response.ok) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        errorCount++;
      }
    }

    // Reload data
    await loadData();
    
    if (successCount > 0) {
      toast.success(`Successfully imported ${successCount} companies`);
    }
    if (errorCount > 0) {
      toast.warning(`Failed to import ${errorCount} companies`);
    }
  };

  // Handle duplicate review confirmation
  const handleConfirmDuplicates = async () => {
    const parsedCompanies = window.tempParsedCompanies || [];
    
    // Add selected duplicates to import list
    const selectedDuplicateCompanies = duplicateCompanies
      .filter((_, index) => selectedDuplicates[index])
      .map(dup => dup.csvData);

    const allCompanies = [...parsedCompanies, ...selectedDuplicateCompanies];

    setShowDuplicateDialog(false);
    
    if (allCompanies.length === 0) {
      toast.info('No companies selected for import');
      return;
    }

    await performImport(allCompanies);
    
    // Cleanup
    window.tempParsedCompanies = null;
    window.tempAdditionalSiteIds = null;
    setDuplicateCompanies([]);
    setSelectedDuplicates({});
  };

  // Download CSV Template
  const handleDownloadTemplate = () => {
    try {
      // Create template with headers and example rows
      const templateContent = [
        'Name,Contact Person,Phone,Email,Address,Company Type,Site Names,Active',
        '"ABC Construction","John Smith","0412 345 678","john@abc.com.au","123 Main St Melbourne VIC 3000","contractor","Thistlethwaite","Yes"',
        '"XYZ Plumbing","Jane Doe","0423 456 789","jane@xyzplumbing.com.au","456 Industrial Rd Sydney NSW 2000","subcontractor","Thistlethwaite; Brighton","Yes"',
        '"Elite Electrical","Bob Johnson","0434 567 890","bob@eliteelec.com.au","789 Business Ave Brisbane QLD 4000","subcontractor","","Yes"',
        '"Roofing Masters","Sarah Wilson","0445 678 901","sarah@roofing.com.au","321 Trade St Perth WA 6000","contractor","Thistlethwaite","No"'
      ].join('\n');
      
      // Create blob and download
      const blob = new Blob([templateContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'company_import_template.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Template downloaded! Fill in your company data and import.');
    } catch (error) {
      console.error('Template download error:', error);
      toast.error('Failed to download template');
    }
  };

  // Area handlers
  const handleCreateArea = async (e) => {
    e.preventDefault();
    if (!areaForm.name.trim()) {
      toast.error("Please enter an area name");
      return;
    }

    // Check for duplicates
    const isDuplicate = areas.some(
      area => area.name.toLowerCase() === areaForm.name.trim().toLowerCase() && area.id !== editingItem?.id
    );
    
    if (isDuplicate) {
      toast.error("This area already exists");
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const url = editingItem 
        ? `${API}/defect-areas/${editingItem.id}`
        : `${API}/defect-areas`;
      
      const response = await fetch(url, {
        method: editingItem ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(areaForm)
      });

      if (response.ok) {
        toast.success(editingItem ? "Area updated successfully" : "Area created successfully");
        setShowAreaDialog(false);
        setEditingItem(null);
        resetAreaForm();
        loadData();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to save area");
      }
    } catch (error) {
      console.error("Error saving area:", error);
      toast.error("Failed to save area");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Element handlers
  const handleCreateElement = async (e) => {
    e.preventDefault();
    if (!elementForm.name.trim()) {
      toast.error("Please enter an element name");
      return;
    }

    // Check for duplicates
    const isDuplicate = elements.some(
      element => element.name.toLowerCase() === elementForm.name.trim().toLowerCase() && element.id !== editingItem?.id
    );
    
    if (isDuplicate) {
      toast.error("This element already exists");
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const url = editingItem 
        ? `${API}/defect-elements/${editingItem.id}`
        : `${API}/defect-elements`;
      
      const response = await fetch(url, {
        method: editingItem ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(elementForm)
      });

      if (response.ok) {
        toast.success(editingItem ? "Element updated successfully" : "Element created successfully");
        setShowElementDialog(false);
        setEditingItem(null);
        resetElementForm();
        loadData();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to save element");
      }
    } catch (error) {
      console.error("Error saving element:", error);
      toast.error("Failed to save element");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Issue handlers
  const handleCreateIssue = async (e) => {
    e.preventDefault();
    if (!issueForm.name.trim()) {
      toast.error("Please enter an issue name");
      return;
    }

    // Check for duplicates
    const isDuplicate = issues.some(
      issue => issue.name.toLowerCase() === issueForm.name.trim().toLowerCase() && issue.id !== editingItem?.id
    );
    
    if (isDuplicate) {
      toast.error("This issue already exists");
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const url = editingItem 
        ? `${API}/defect-issues/${editingItem.id}`
        : `${API}/defect-issues`;
      
      const response = await fetch(url, {
        method: editingItem ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(issueForm)
      });

      if (response.ok) {
        toast.success(editingItem ? "Issue updated successfully" : "Issue created successfully");
        setShowIssueDialog(false);
        setEditingItem(null);
        resetIssueForm();
        loadData();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to save issue");
      }
    } catch (error) {
      console.error("Error saving issue:", error);
      toast.error("Failed to save issue");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="manage-lists-loading">
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 gap-6">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="h-20" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Only allow access to admins and site managers
  if (user?.role === 'supervisor') {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="dashboard-card">
          <CardContent className="p-12 text-center">
            <Settings className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Access Restricted</h3>
            <p className="text-slate-600">Only administrators and site managers can manage trades and companies.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 fade-in-up" data-testid="manage-lists-container">
      {/* Header */}
      <div className="mb-8">
        <h1 className="heading-primary text-4xl mb-2">Manage Lists</h1>
        <p className="text-lg text-slate-600">Manage trades, companies, and defect options for dropdown selections</p>
      </div>

      {/* Search/Filter */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(value) => {
        setActiveTab(value);
        setSearchTerm(""); // Clear search when switching tabs
      }} className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="trades" className="flex items-center space-x-2" data-testid="trades-tab">
            <Briefcase className="w-4 h-4" />
            <span>Trades</span>
          </TabsTrigger>
          <TabsTrigger value="companies" className="flex items-center space-x-2" data-testid="companies-tab">
            <Building className="w-4 h-4" />
            <span>Companies</span>
          </TabsTrigger>
          <TabsTrigger value="areas" className="flex items-center space-x-2">
            <Settings className="w-4 h-4" />
            <span>Areas</span>
          </TabsTrigger>
          <TabsTrigger value="elements" className="flex items-center space-x-2">
            <Settings className="w-4 h-4" />
            <span>Elements</span>
          </TabsTrigger>
          <TabsTrigger value="issues" className="flex items-center space-x-2">
            <Settings className="w-4 h-4" />
            <span>Issues</span>
          </TabsTrigger>
        </TabsList>

        {/* Trades Tab */}
        <TabsContent value="trades">
          <Card className="dashboard-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="heading-secondary">Trades Management</CardTitle>
              <Button onClick={() => setShowTradeDialog(true)} className="btn-construction" data-testid="add-trade-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Trade
              </Button>
            </CardHeader>
            <CardContent>
              {trades.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Briefcase className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No trades found</p>
                  <p className="text-sm">Add your first trade to get started</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-100 border-b-2 border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Name</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Category</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Description</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Status</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {[...trades]
                        .filter(trade => trade.name.toLowerCase().includes(searchTerm.toLowerCase()))
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((trade) => (
                        <tr key={trade.id} className="hover:bg-slate-50 transition-colors" data-testid={`trade-item-${trade.id}`}>
                          <td className="px-4 py-3 text-sm font-medium text-slate-900">{trade.name}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            <Badge className="text-xs bg-blue-100 text-blue-700">
                              {tradeCategories.find(c => c.value === trade.category)?.label || trade.category}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">{trade.description || '-'}</td>
                          <td className="px-4 py-3 text-sm">
                            <Badge className={`text-xs ${trade.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {trade.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEditTrade(trade)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit Trade
                                </DropdownMenuItem>
                                {user?.role === 'admin' && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem 
                                      onClick={() => {
                                        setDeleteItem({...trade, type: 'trade'});
                                        setShowDeleteDialog(true);
                                      }}
                                      className="text-red-600 focus:text-red-600"
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete Trade
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Companies Tab */}
        <TabsContent value="companies">
          <Card className="dashboard-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="heading-secondary">Companies Management</CardTitle>
              <div className="flex gap-2">
                <Button 
                  onClick={handleExportCompanies} 
                  variant="outline" 
                  size="sm"
                  disabled={companies.length === 0}
                  title="Export all companies to CSV"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
                <Button 
                  onClick={handleDownloadTemplate} 
                  variant="outline" 
                  size="sm"
                  title="Download CSV template with examples"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Template
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowImportDialog(true)}
                  title="Import companies from CSV file"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import CSV
                </Button>
                <Button onClick={() => setShowCompanyDialog(true)} className="btn-construction" data-testid="add-company-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Company
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {companies.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Building className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No companies found</p>
                  <p className="text-sm">Add your first company to get started</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-100 border-b-2 border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Name</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Type</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Contact Person</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Phone</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Site(s) Allocated</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Status</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {[...companies]
                        .filter(company => company.name.toLowerCase().includes(searchTerm.toLowerCase()))
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((company) => (
                        <tr key={company.id} className="hover:bg-slate-50 transition-colors" data-testid={`company-item-${company.id}`}>
                          <td className="px-4 py-3 text-sm font-medium text-slate-900">{company.name}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            <Badge className="text-xs bg-blue-100 text-blue-700">
                              {companyTypes.find(c => c.value === company.company_type)?.label || company.company_type}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">{company.contact_person || '-'}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{company.phone || '-'}</td>
                          <td className="px-4 py-3 text-sm" data-testid={`company-sites-${company.id}`}>
                            {(() => {
                              const siteNames = getCompanySiteNames(company);
                              if (siteNames.length === 0) {
                                return (
                                  <Badge className="text-xs bg-amber-100 text-amber-700">
                                    Unallocated
                                  </Badge>
                                );
                              }
                              return (
                                <div className="flex flex-wrap gap-1">
                                  {siteNames.map((name, i) => (
                                    <Badge key={i} className="text-xs bg-slate-100 text-slate-700">
                                      {name}
                                    </Badge>
                                  ))}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <Badge className={`text-xs ${company.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {company.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEditCompany(company)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit Company
                                </DropdownMenuItem>
                                {user?.role === 'admin' && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem 
                                      onClick={() => {
                                        setDeleteItem({...company, type: 'company'});
                                        setShowDeleteDialog(true);
                                      }}
                                      className="text-red-600 focus:text-red-600"
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete Company
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Areas Tab */}
        <TabsContent value="areas">
          <Card className="dashboard-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="heading-secondary">Defect Areas</CardTitle>
              <Button onClick={() => setShowAreaDialog(true)} className="btn-construction">
                <Plus className="w-4 h-4 mr-2" />
                Add Area
              </Button>
            </CardHeader>
            <CardContent>
              {areas.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Settings className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No areas found</p>
                  <p className="text-sm">Add your first area to get started</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-100 border-b-2 border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Name</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {[...areas]
                        .filter(area => area.name.toLowerCase().includes(searchTerm.toLowerCase()))
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((area) => (
                        <tr key={area.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3 text-sm font-medium text-slate-900">{area.name}</td>
                          <td className="px-4 py-3 text-center">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => {
                                  setEditingItem(area);
                                  setAreaForm({ name: area.name });
                                  setShowAreaDialog(true);
                                }}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  className="text-red-600"
                                  onClick={() => {
                                    setDeleteItem({ id: area.id, name: area.name, type: "area" });
                                    setShowDeleteDialog(true);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Elements Tab */}
        <TabsContent value="elements">
          <Card className="dashboard-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="heading-secondary">Defect Elements</CardTitle>
              <Button onClick={() => setShowElementDialog(true)} className="btn-construction">
                <Plus className="w-4 h-4 mr-2" />
                Add Element
              </Button>
            </CardHeader>
            <CardContent>
              {elements.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Settings className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No elements found</p>
                  <p className="text-sm">Add your first element to get started</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-100 border-b-2 border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Name</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {[...elements]
                        .filter(element => element.name.toLowerCase().includes(searchTerm.toLowerCase()))
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((element) => (
                        <tr key={element.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3 text-sm font-medium text-slate-900">{element.name}</td>
                          <td className="px-4 py-3 text-center">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => {
                                  setEditingItem(element);
                                  setElementForm({ name: element.name });
                                  setShowElementDialog(true);
                                }}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  className="text-red-600"
                                  onClick={() => {
                                    setDeleteItem({ id: element.id, name: element.name, type: "element" });
                                    setShowDeleteDialog(true);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Issues Tab */}
        <TabsContent value="issues">
          <Card className="dashboard-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="heading-secondary">Defect Issues</CardTitle>
              <Button onClick={() => setShowIssueDialog(true)} className="btn-construction">
                <Plus className="w-4 h-4 mr-2" />
                Add Issue
              </Button>
            </CardHeader>
            <CardContent>
              {issues.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Settings className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No issues found</p>
                  <p className="text-sm">Add your first issue to get started</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-100 border-b-2 border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Name</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {[...issues]
                        .filter(issue => issue.name.toLowerCase().includes(searchTerm.toLowerCase()))
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((issue) => (
                        <tr key={issue.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3 text-sm font-medium text-slate-900">{issue.name}</td>
                          <td className="px-4 py-3 text-center">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => {
                                  setEditingItem(issue);
                                  setIssueForm({ name: issue.name });
                                  setShowIssueDialog(true);
                                }}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  className="text-red-600"
                                  onClick={() => {
                                    setDeleteItem({ id: issue.id, name: issue.name, type: "issue" });
                                    setShowDeleteDialog(true);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Trade Dialog */}
      <Dialog open={showTradeDialog} onOpenChange={(open) => {
        setShowTradeDialog(open);
        if (!open) {
          setEditingItem(null);
          resetTradeForm();
        }
      }}>
        <DialogContent className="sm:max-w-[500px]" data-testid="trade-dialog">
          <DialogHeader>
            <DialogTitle className="heading-secondary">
              {editingItem ? 'Edit Trade' : 'Add New Trade'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTrade} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="trade-name" className="font-medium">Trade Name *</Label>
                <Input
                  id="trade-name"
                  placeholder="e.g., Carpenter, Electrician"
                  value={tradeForm.name}
                  onChange={(e) => setTradeForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                  data-testid="trade-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="font-medium">Category</Label>
                <Select value={tradeForm.category} onValueChange={(value) => setTradeForm(prev => ({ ...prev, category: value }))}>
                  <SelectTrigger data-testid="trade-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {tradeCategories.map((category) => (
                      <SelectItem key={category.value} value={category.value}>
                        {category.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="trade-description" className="font-medium">Description</Label>
                <Textarea
                  id="trade-description"
                  placeholder="Brief description of the trade..."
                  value={tradeForm.description}
                  onChange={(e) => setTradeForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  data-testid="trade-description-input"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="trade-active"
                  checked={tradeForm.is_active}
                  onChange={(e) => setTradeForm(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="rounded"
                />
                <Label htmlFor="trade-active" className="font-medium">Active</Label>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowTradeDialog(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="btn-construction"
                disabled={isSubmitting}
                data-testid="save-trade-button"
              >
                {isSubmitting ? "Saving..." : (editingItem ? "Update Trade" : "Add Trade")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Company Dialog */}
      <Dialog open={showCompanyDialog} onOpenChange={(open) => {
        setShowCompanyDialog(open);
        if (!open) {
          setEditingItem(null);
          resetCompanyForm();
        }
      }}>
        <DialogContent className="sm:max-w-[600px]" data-testid="company-dialog">
          <DialogHeader>
            <DialogTitle className="heading-secondary">
              {editingItem ? 'Edit Company' : 'Add New Company'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCompany} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="company-name" className="font-medium">Company Name *</Label>
                <Input
                  id="company-name"
                  placeholder="e.g., ABC Construction Ltd"
                  value={companyForm.name}
                  onChange={(e) => setCompanyForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                  data-testid="company-name-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-medium">Company Type</Label>
                  <Select value={companyForm.company_type} onValueChange={(value) => setCompanyForm(prev => ({ ...prev, company_type: value }))}>
                    <SelectTrigger data-testid="company-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {companyTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="font-medium">Associated Sites</Label>
                  <MultiSelect
                    options={sites.map(site => ({ value: site.id, label: site.name }))}
                    selected={companyForm.site_ids}
                    onChange={(selected) => setCompanyForm(prev => ({ ...prev, site_ids: selected }))}
                    placeholder="Select sites"
                  />
                  <p className="text-xs text-slate-500">Optional: Link this company to specific sites</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact-person" className="font-medium">Contact Person</Label>
                  <Input
                    id="contact-person"
                    placeholder="Primary contact name"
                    value={companyForm.contact_person}
                    onChange={(e) => setCompanyForm(prev => ({ ...prev, contact_person: e.target.value }))}
                    data-testid="contact-person-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company-phone" className="font-medium">Phone</Label>
                  <Input
                    id="company-phone"
                    placeholder="+1 (555) 123-4567"
                    value={companyForm.phone}
                    onChange={(e) => setCompanyForm(prev => ({ ...prev, phone: e.target.value }))}
                    data-testid="company-phone-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="company-email" className="font-medium">Email</Label>
                  <Input
                    id="company-email"
                    type="email"
                    placeholder="contact@company.com"
                    value={companyForm.email}
                    onChange={(e) => setCompanyForm(prev => ({ ...prev, email: e.target.value }))}
                    data-testid="company-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="company-address" className="font-medium">Address</Label>
                <Textarea
                  id="company-address"
                  placeholder="Company address..."
                  value={companyForm.address}
                  onChange={(e) => setCompanyForm(prev => ({ ...prev, address: e.target.value }))}
                  rows={2}
                  data-testid="company-address-input"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="company-active"
                  checked={companyForm.is_active}
                  onChange={(e) => setCompanyForm(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="rounded"
                />
                <Label htmlFor="company-active" className="font-medium">Active</Label>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCompanyDialog(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="btn-construction"
                disabled={isSubmitting}
                data-testid="save-company-button"
              >
                {isSubmitting ? "Saving..." : (editingItem ? "Update Company" : "Add Company")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Area Dialog */}
      <Dialog open={showAreaDialog} onOpenChange={(open) => {
        setShowAreaDialog(open);
        if (!open) {
          setEditingItem(null);
          resetAreaForm();
        }
      }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="heading-secondary">
              {editingItem ? 'Edit Area' : 'Add New Area'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateArea} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="area-name" className="font-medium">Area Name *</Label>
                <Input
                  id="area-name"
                  placeholder="e.g., Basement, Living Room"
                  value={areaForm.name}
                  onChange={(e) => setAreaForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAreaDialog(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="btn-construction"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : (editingItem ? "Update Area" : "Add Area")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Element Dialog */}
      <Dialog open={showElementDialog} onOpenChange={(open) => {
        setShowElementDialog(open);
        if (!open) {
          setEditingItem(null);
          resetElementForm();
        }
      }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="heading-secondary">
              {editingItem ? 'Edit Element' : 'Add New Element'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateElement} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="element-name" className="font-medium">Element Name *</Label>
                <Input
                  id="element-name"
                  placeholder="e.g., Door, Window, Ceiling"
                  value={elementForm.name}
                  onChange={(e) => setElementForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowElementDialog(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="btn-construction"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : (editingItem ? "Update Element" : "Add Element")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Issue Dialog */}
      <Dialog open={showIssueDialog} onOpenChange={(open) => {
        setShowIssueDialog(open);
        if (!open) {
          setEditingItem(null);
          resetIssueForm();
        }
      }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="heading-secondary">
              {editingItem ? 'Edit Issue' : 'Add New Issue'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateIssue} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="issue-name" className="font-medium">Issue Name *</Label>
                <Input
                  id="issue-name"
                  placeholder="e.g., Missing, Damaged, Crack"
                  value={issueForm.name}
                  onChange={(e) => setIssueForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowIssueDialog(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="btn-construction"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : (editingItem ? "Update Issue" : "Add Issue")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent data-testid="delete-confirmation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Delete {deleteItem?.type}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteItem?.name}</strong>?
              <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm text-red-800">
                  <strong>Warning:</strong> If this {deleteItem?.type} is being used in existing records, it will be marked as inactive instead of deleted.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={isSubmitting}
              data-testid="confirm-delete"
            >
              {isSubmitting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Companies Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import Companies from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-medium">Select CSV File</Label>
              <Input
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="mt-2"
              />
              <p className="text-xs text-slate-500 mt-1">
                Choose a CSV file with company data
              </p>
            </div>
            
            <div>
              <Label className="font-medium">Assign to Sites (Optional)</Label>
              <MultiSelect
                options={sites.map(site => ({ value: site.id, label: site.name }))}
                selected={selectedImportSites}
                onChange={setSelectedImportSites}
                placeholder="Select sites to assign all companies"
              />
              <p className="text-xs text-slate-500 mt-1">
                All imported companies will be assigned to these sites (in addition to any sites in the CSV)
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowImportDialog(false);
                  setImportFile(null);
                  setSelectedImportSites([]);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (!importFile) {
                    toast.error('Please select a CSV file');
                    return;
                  }
                  setShowImportDialog(false);
                  await handleImportCompanies(importFile, selectedImportSites);
                  setImportFile(null);
                  setSelectedImportSites([]);
                }}
                disabled={!importFile}
                className="flex-1 btn-construction"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate Review Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={(open) => {
        if (!open) {
          setShowDuplicateDialog(false);
          setDuplicateCompanies([]);
          setSelectedDuplicates({});
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Duplicate Companies Found</DialogTitle>
            <p className="text-sm text-slate-600">
              {duplicateCompanies.length} companies in the CSV match existing company names. 
              Select which duplicates you want to import anyway.
            </p>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              {duplicateCompanies.map((dup, index) => (
                <div key={index} className="border rounded-lg p-4 space-y-2">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id={`duplicate-${index}`}
                      checked={selectedDuplicates[index] || false}
                      onChange={(e) => {
                        setSelectedDuplicates(prev => ({
                          ...prev,
                          [index]: e.target.checked
                        }));
                      }}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <label htmlFor={`duplicate-${index}`} className="cursor-pointer">
                        <div className="font-semibold text-amber-600 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          {dup.csvData.name}
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
                          <div>
                            <p className="text-slate-500 font-medium">Existing Company:</p>
                            <p className="text-slate-700">Type: {dup.existing.company_type}</p>
                            <p className="text-slate-700">Contact: {dup.existing.contact_person || 'N/A'}</p>
                            <p className="text-slate-700">Phone: {dup.existing.phone || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-slate-500 font-medium">CSV Import Data:</p>
                            <p className="text-slate-700">Type: {dup.csvData.company_type}</p>
                            <p className="text-slate-700">Contact: {dup.csvData.contact_person || 'N/A'}</p>
                            <p className="text-slate-700">Phone: {dup.csvData.phone || 'N/A'}</p>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  // Select all
                  const allSelected = {};
                  duplicateCompanies.forEach((_, index) => {
                    allSelected[index] = true;
                  });
                  setSelectedDuplicates(allSelected);
                }}
                size="sm"
              >
                Select All
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  // Deselect all
                  setSelectedDuplicates({});
                }}
                size="sm"
              >
                Deselect All
              </Button>
              <div className="flex-1"></div>
              <Button
                variant="outline"
                onClick={() => {
                  setShowDuplicateDialog(false);
                  setDuplicateCompanies([]);
                  setSelectedDuplicates({});
                  window.tempParsedCompanies = null;
                  window.tempAdditionalSiteIds = null;
                }}
              >
                Cancel Import
              </Button>
              <Button
                onClick={handleConfirmDuplicates}
                className="btn-construction"
              >
                Continue Import ({Object.values(selectedDuplicates).filter(Boolean).length} duplicates selected)
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}